import { Loader2, Paperclip, SendHorizontal, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  COMMENT_FILE_ACCEPT,
  MAX_COMMENT_FILES,
  MAX_COMMENT_FILE_BYTES,
  shiftPicks,
  tokenisedBody,
  type Mentionable,
  type MentionPick,
  type WriteRejection,
} from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { describeRejection, fileSize } from "@/modules/comments/format";

/** What a composer hands back when it is submitted. */
export interface ComposerSubmit {
  /** The text with `@[userId]` tokens, ready to send. */
  body: string;
  files: File[];
}

/** The `@name` being typed, and where its `@` is. */
interface MentionQuery {
  start: number;
  text: string;
}

/** Longest name fragment that still counts as someone being looked up. */
const MAX_QUERY_LENGTH = 30;
const MAX_SUGGESTIONS = 6;

/**
 * The `@` being typed at `caret`, if there is one.
 *
 * An `@` starts a lookup only at the start of the text or after a space, so an
 * email address typed into a comment is not read as one; and the lookup ends at
 * a line break. Names have spaces in them ("Asha Mehta"), so a space does not
 * end it — the list simply empties when what follows matches no one.
 */
function mentionQueryAt(value: string, caret: number): MentionQuery | null {
  const before = value.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start === -1) return null;
  const preceding = before[start - 1];
  if (preceding !== undefined && !/\s/.test(preceding)) return null;
  const text = before.slice(start + 1);
  if (text.includes("\n") || text.length > MAX_QUERY_LENGTH) return null;
  return { start, text };
}

function matches(people: readonly Mentionable[], query: MentionQuery) {
  const needle = query.text.toLowerCase();
  return people
    .filter((person) => person.name.toLowerCase().includes(needle))
    .slice(0, MAX_SUGGESTIONS);
}

/** What is wrong with a file before it is sent, or `null`. The server decides again. */
function fileProblem(file: File): string | null {
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > MAX_COMMENT_FILE_BYTES) {
    return `"${file.name}" is over ${String(MAX_COMMENT_FILE_BYTES / 1024 / 1024)} MB.`;
  }
  const accepted = COMMENT_FILE_ACCEPT.split(",");
  if (file.type !== "" && !accepted.includes(file.type)) {
    return `"${file.name}" is not an image or a PDF.`;
  }
  return null;
}

/**
 * The box a comment is written in, for a new comment and for editing one
 * (#448).
 *
 * `@` opens a list of active users; choosing one puts `@Name` in the text and
 * remembers who it is *and where* — the id of the person chosen, at the place in
 * the draft — and on submit each remembered `@Name` still standing there becomes
 * the `@[userId]` token the server stores. The place is kept as the draft
 * changes (`shiftPicks`), so two colleagues with one name are two mentions, each
 * resolved to the person actually picked and never looked up by name again.
 * Someone typed by hand without choosing from the list stays plain text, so a
 * mention is always a person somebody picked.
 *
 * Draft, files, pending and the last refusal live here. `submit` answers `null`
 * when the write was accepted — the draft is cleared — or the rejection to show,
 * and the draft is kept either way it fails. Files are for a new comment only: a
 * comment's files are fixed once it is posted.
 */
export function CommentComposer({
  people,
  initialDraft = { text: "", picks: [] },
  allowFiles,
  allowEmpty = false,
  submitLabel,
  placeholder,
  label,
  autoFocus = false,
  submit,
  onCancel,
}: {
  people: readonly Mentionable[];
  /** What the box starts with: an edit's text, and the people already in it. */
  initialDraft?: { text: string; picks: MentionPick[] };
  allowFiles: boolean;
  /** An edit may leave no text when the comment has a file. */
  allowEmpty?: boolean;
  submitLabel: string;
  placeholder?: string;
  /**
   * What the box is called to a screen reader. Absent, it follows `allowFiles`:
   * a box that takes files is a new comment, one that does not is an edit. A box
   * with no files that is *not* an edit — a task's — says so here.
   */
  label?: string;
  autoFocus?: boolean;
  submit: (input: ComposerSubmit) => Promise<WriteRejection | null>;
  onCancel?: () => void;
}) {
  const listId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState(initialDraft.text);
  const [picks, setPicks] = useState<MentionPick[]>(initialDraft.picks);
  const [files, setFiles] = useState<File[]>([]);
  const [query, setQuery] = useState<MentionQuery | null>(null);
  const [active, setActive] = useState(0);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const suggestions = query ? matches(people, query) : [];
  const open = suggestions.length > 0;

  /*
    Escape closes the list and nothing else. Comments are written inside a
    drawer, and the drawer listens for Escape on the document before this box
    could see it — so a handler on the box would close the list and the whole
    drawer, draft and all. This one sits on the window, ahead of the document,
    and stops the key there, only while the list is open.
  */
  useEffect(() => {
    if (!open) return;
    const closeList = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setQuery(null);
    };
    window.addEventListener("keydown", closeList, true);
    return () => {
      window.removeEventListener("keydown", closeList, true);
    };
  }, [open]);
  const empty = text.trim() === "" && files.length === 0 && !allowEmpty;

  function choose(person: Mentionable) {
    if (!query) return;
    const textarea = textareaRef.current;
    const caret = textarea?.selectionStart ?? text.length;
    const inserted = `@${person.name} `;
    const next = text.slice(0, query.start) + inserted + text.slice(caret);
    setText(next);
    // The mentions around this one move with the text; this one is added at
    // the place it was typed. Two people may share a name, so the same person
    // twice is two picks and a different person with the same name is too.
    setPicks((current) => [
      ...shiftPicks(text, next, current),
      { userId: person.id, name: person.name, start: query.start },
    ]);
    setQuery(null);
    const position = query.start + inserted.length;
    // After React has put the new text in the box.
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(position, position);
    });
  }

  function addFiles(chosen: FileList | null) {
    if (!chosen || chosen.length === 0) return;
    const next = [...files];
    let refused: string | null = null;
    for (const file of Array.from(chosen)) {
      const already = next.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified,
      );
      if (already) continue;
      if (next.length >= MAX_COMMENT_FILES) {
        refused = `At most ${String(MAX_COMMENT_FILES)} files on a comment.`;
        break;
      }
      const wrong = fileProblem(file);
      if (wrong) {
        refused = wrong;
        continue;
      }
      next.push(file);
    }
    setFiles(next);
    setProblem(refused);
  }

  async function send() {
    if (pending || empty) return;
    setPending(true);
    setProblem(null);
    try {
      const rejection = await submit({
        body: tokenisedBody(text, picks),
        files,
      });
      if (rejection) {
        setProblem(describeRejection(rejection));
      } else if (!onCancel) {
        // A new comment: the box is ready for the next one. An edit is closed
        // by its owner, which unmounts this.
        setText("");
        setPicks([]);
        setFiles([]);
        setQuery(null);
      }
    } catch {
      setProblem("Could not reach the server. Try again.");
    } finally {
      setPending(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((index) => (index + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActive(
          (index) => (index - 1 + suggestions.length) % suggestions.length,
        );
        return;
      }
      if (
        (event.key === "Enter" && !event.ctrlKey && !event.metaKey) ||
        event.key === "Tab"
      ) {
        const person = suggestions[Math.min(active, suggestions.length - 1)];
        if (person) {
          event.preventDefault();
          choose(person);
          return;
        }
      }
    }
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          autoFocus={autoFocus}
          rows={3}
          value={text}
          disabled={pending}
          placeholder={placeholder}
          aria-label={
            label ?? (allowFiles ? "Write a comment" : "Edit comment")
          }
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            open ? `${listId}-${String(active)}` : undefined
          }
          className="min-h-20"
          onChange={(event) => {
            const next = event.target.value;
            setPicks((current) => shiftPicks(text, next, current));
            setText(next);
            setQuery(
              mentionQueryAt(event.target.value, event.target.selectionStart),
            );
            setActive(0);
          }}
          onSelect={(event) => {
            setQuery(
              mentionQueryAt(
                event.currentTarget.value,
                event.currentTarget.selectionStart,
              ),
            );
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setQuery(null);
          }}
        />
        {open ? (
          <ul
            id={listId}
            role="listbox"
            aria-label="Mention someone"
            className="bg-popover text-popover-foreground absolute right-0 left-0 z-20 mt-1 max-h-56 overflow-auto rounded-md border p-1 shadow-md"
          >
            {suggestions.map((person, index) => (
              <li
                key={person.id}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === active}
                // `mousedown`, not `click`: the box loses focus on mousedown and
                // would close the list before a click landed.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(person);
                }}
                onMouseEnter={() => {
                  setActive(index);
                }}
                className={cn(
                  "flex min-h-9 cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm",
                  index === active && "bg-accent text-accent-foreground",
                )}
              >
                {person.name}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {files.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Files to attach">
          {files.map((file) => (
            <li
              key={`${file.name}-${String(file.size)}-${String(file.lastModified)}`}
              className="bg-muted/60 flex max-w-full items-center gap-1.5 rounded-md border py-1 pr-1 pl-2 text-xs"
            >
              <span className="min-w-0 truncate">{file.name}</span>
              <span className="text-muted-foreground shrink-0">
                {fileSize(file.size)}
              </span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={pending}
                onClick={() => {
                  setFiles((current) =>
                    current.filter((candidate) => candidate !== file),
                  );
                }}
                className="hover:bg-secondary-foreground/10 focus-visible:ring-ring flex size-6 shrink-0 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:outline-none"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {allowFiles ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={COMMENT_FILE_ACCEPT}
              className="hidden"
              aria-label="Attach images or PDFs"
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pending || files.length >= MAX_COMMENT_FILES}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip aria-hidden />
              Attach
            </Button>
          </>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {onCancel ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending}
              onClick={onCancel}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={pending || empty}
            onClick={() => {
              void send();
            }}
          >
            {pending ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <SendHorizontal aria-hidden />
            )}
            {submitLabel}
          </Button>
        </div>
      </div>

      {allowFiles ? (
        <p className="text-muted-foreground text-xs">
          Type @ to mention a teammate. Images and PDFs, up to{" "}
          {String(MAX_COMMENT_FILES)} files of{" "}
          {String(MAX_COMMENT_FILE_BYTES / 1024 / 1024)} MB.
        </p>
      ) : null}
      {problem ? (
        <p role="alert" className="text-destructive text-sm">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
